import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/react-hooks";
import { Box, FormHelperText } from "@mui/material";
import { MY_HR_CHANGE_PASSWORD } from "my-hr/graphql";
import { useMyHrLastPasswordChange } from "my-hr/hooks";
import {
  OcCard,
  OcCardButtonBar,
  OcConfirmationDialog,
  OcPasswordInputField,
} from "oc/components";
import { useOcPasswordInput, useOcProperty, useOcSnackbar } from "oc/hooks";

interface MyHrPasswordCardProps {
  email: string;
  isMobile: boolean;
}

export const MyHrPasswordCard: React.FC<MyHrPasswordCardProps> = ({
  email,
  isMobile,
}) => {
  const { t } = useTranslation();

  const { data: passwordPolicy } = useOcProperty({
    resource: "system",
    key: "passwordPolicy",
  });

  const { setOpenMessage } = useOcSnackbar();

  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const { data } = useMyHrLastPasswordChange({ email });
  const lastPasswordChange = data
    ? new Date(data).toLocaleString()
    : "";

  const {
    value: password,
    setValue: setPassword,
    isValid: isPasswordValid,
    validate: validatePassword,
    error: passwordError,
    setError: setPasswordError,
    helperText: passwordHelperText,
    setHelperText: setPasswordHelperText,
  } = useOcPasswordInput();

  const {
    value: passwordAgain,
    setValue: setPasswordAgain,
    isValid: isPasswordAgainValid,
    validate: validatePasswordAgain,
    error: passwordAgainError,
    setError: setPasswordAgainError,
    helperText: passwordAgainHelperText,
    setHelperText: setPasswordAgainHelperText,
  } = useOcPasswordInput();

  const {
    value: currentPassword,
    setValue: setCurrentPassword,
    isValid: isCurrentPasswordValid,
    validate: validateCurrentPassword,
    error: currentPasswordError,
    setError: setCurrentPasswordError,
    helperText: currentPasswordHelperText,
    setHelperText: setCurrentPasswordHelperText,
  } = useOcPasswordInput();

  // GraphQL mutation
  const [changePassword] = useMutation(MY_HR_CHANGE_PASSWORD, {
    onCompleted: () => {
      setLoading(false);
      setPassword("");
      setPasswordAgain("");
      setCurrentPassword("");
      setOpenMessage({
        type: "success",
        message: t("MY_HR_PASSWORD_CHANGE_SUCCEEDED"),
      });
    },
    onError: (error) => {
      setLoading(false);
      if (error.message === "Wrong password!") {
        setCurrentPasswordError(true);
        setPasswordError(true);
        setCurrentPasswordHelperText(t("MY_HR_ERROR_MESSAGE_WRONG_PASSWORD"));
        setPasswordHelperText(t("MY_HR_ERROR_MESSAGE_PASSWORD_IS_WRONG"));
      }
    },
  });

  const handleCancel = () => {
    setPassword("");
    setPasswordAgain("");
    setCurrentPassword("");
    setCurrentPasswordError(false);
    setPasswordError(false);
    setPasswordAgainError(false);
    setCurrentPasswordHelperText("");
    setPasswordHelperText("");
    setPasswordAgainHelperText("");
  };
  const doSave = () => {
    let valid = true;

    if (!isPasswordValid({ checkPasswordPolicy: true })) {
      validatePassword({ checkPasswordPolicy: true });
      valid = false;
    }
    if (!isPasswordAgainValid({ checkPasswordPolicy: true })) {
      validatePasswordAgain({ checkPasswordPolicy: true });
      valid = false;
    }
    if (!isCurrentPasswordValid({ checkPasswordPolicy: false })) {
      validateCurrentPassword({ checkPasswordPolicy: false });
      valid = false;
    }

    if (password !== passwordAgain) {
      setPasswordError(true);
      setPasswordHelperText(t("MY_HR_ERROR_MESSAGE_WRONG_PASSWORD"));
      setPasswordAgainError(true);
      setPasswordAgainHelperText(
        t("MY_HR_ERROR_MESSAGE_TWO_PASSWORD_IS_DIFFERENT")
      );
      valid = false;
    }

    if (password && password === currentPassword) {
      setPasswordError(true);
      setPasswordHelperText(t("MY_HR_NOT_SAME_PASSWORD"));
      valid = false;
    }

    if (!valid) {
      setShowConfirmDialog(false);
      return;
    }

    setLoading(true);
    changePassword({
      variables: {
        email,
        oldPassword: currentPassword,
        newPassword: password,
      },
    });
    setShowConfirmDialog(false);
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    doSave();
  };

  const handleDialogCancel = () => {
    setShowConfirmDialog(false);
  };

  return (
    <>
      <OcConfirmationDialog
        showDialog={showConfirmDialog}
        onClose={doSave}
        onCancel={handleDialogCancel}
        title={t("OC_CONFIRM_SETTING")}
        texts={[t("OC_CONFIRM_SETTING_INFO")]}
        okButtonText={t("OC_ALL_RIGHT")}
        cancelButtonText={t("OC_CANCEL")}
      />
      <OcCard label={t("MY_HR_PASSWORD")} loading={loading}>
        <Box padding="16px" width={isMobile ? "auto" : "464px"}>
          <form onSubmit={handleSave}>
            <Box
              paddingBottom="36px"
              fontSize="12px"
              letterSpacing="0.4px"
              color="#4d4d4d"
            >
              {passwordPolicy === "strong"
                ? t("MY_HR_INFO_TEXT_STRONG_PASSWORD")
                : t("MY_HR_INFO_TEXT_NOT_STRONG_PASSWORD")}
            </Box>
            <OcPasswordInputField
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={passwordError}
              helperText={passwordHelperText}
              label={t("MY_HR_NEW_PASSWORD")}
              setError={setPasswordError}
              setHelperText={setPasswordHelperText}
            />
            {!passwordError && lastPasswordChange && (
              <Box paddingLeft="14px">
                <FormHelperText>
                  {t("MY_HR_GET_LAST_PASSWORD_CHANGE")}: {lastPasswordChange}
                </FormHelperText>
              </Box>
            )}
            <Box height="36px" />
            <OcPasswordInputField
              value={passwordAgain}
              onChange={(e) => setPasswordAgain(e.target.value)}
              error={passwordAgainError}
              helperText={passwordAgainHelperText}
              label={t("MY_HR_CONFIRM_NEW_PASSWORD")}
              setError={setPasswordAgainError}
              setHelperText={setPasswordAgainHelperText}
            />
            <Box height="36px" style={{ borderBottom: "1px solid #C6CACC" }} />
            <Box height="36px" />
            <OcPasswordInputField
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              error={currentPasswordError}
              helperText={currentPasswordHelperText}
              label={t("MY_HR_CURRENT_PASSWORD")}
              setError={setCurrentPasswordError}
              setHelperText={setCurrentPasswordHelperText}
            />
            <Box height="20px" />
          </form>
        </Box>
        <OcCardButtonBar
          handleRightButton={handleCancel}
          rightButtonColor="secondary"
          rightButtonText={t("OC_CANCEL")}
          handlePrimaryButton={() => setShowConfirmDialog(true)}
          primaryButtonText={t("OC_SAVE")}
          primaryButtonDisabled={!password || !passwordAgain}
        />
      </OcCard>
    </>
  );
};

export default MyHrPasswordCard;
